/**
 * Platform Detection Utility
 *
 * Provides a robust, build-time platform detection mechanism that avoids
 * runtime errors and bundler issues. Uses compile-time constants and
 * environment variables for explicit platform identification.
 */
/**
 * Platform type enumeration
 */
export var Platform;
(function (Platform) {
    Platform["WEB"] = "web";
    Platform["MOBILE"] = "mobile";
    Platform["UNKNOWN"] = "unknown";
})(Platform || (Platform = {}));
/**
 * Detect the current platform using multiple detection strategies
 * Priority order:
 * 1. Build-time environment variable (REACT_APP_PLATFORM or VITE_PLATFORM)
 * 2. React Native specific globals (__DEV__, global.HermesInternal)
 * 3. Package.json "react-native" field presence (for Metro bundler)
 * 4. Navigator.product (legacy, least reliable)
 */
function detectPlatform() {
    // Strategy 1: Build-time environment variable (most reliable)
    if (typeof process !== 'undefined' && process.env) {
        const envPlatform = process.env.REACT_APP_PLATFORM || process.env.VITE_PLATFORM;
        if (envPlatform === 'mobile' || envPlatform === 'native') {
            return Platform.MOBILE;
        }
        if (envPlatform === 'web') {
            return Platform.WEB;
        }
    }
    // Strategy 2: React Native globals (reliable for RN environment)
    if (typeof global !== 'undefined') {
        // Check for Hermes engine (React Native)
        if (global.HermesInternal !== undefined) {
            return Platform.MOBILE;
        }
        // Check for __DEV__ flag (React Native)
        if (typeof global.__DEV__ !== 'undefined') {
            return Platform.MOBILE;
        }
    }
    // Strategy 3: Check for React Native WebView
    if (typeof window !== 'undefined' && window.ReactNativeWebView) {
        return Platform.MOBILE;
    }
    // Strategy 4: Navigator.product (legacy, kept for backward compatibility)
    if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
        return Platform.MOBILE;
    }
    // Default to web if we have a DOM
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        return Platform.WEB;
    }
    return Platform.UNKNOWN;
}
// Detect platform once at module load time
const CURRENT_PLATFORM = detectPlatform();
/**
 * Get the current platform
 */
export function getCurrentPlatform() {
    return CURRENT_PLATFORM;
}
/**
 * Check if running on web platform
 */
export function isWeb() {
    return CURRENT_PLATFORM === Platform.WEB;
}
/**
 * Check if running on mobile platform (React Native)
 */
export function isMobile() {
    return CURRENT_PLATFORM === Platform.MOBILE;
}
/**
 * Check if platform is unknown (SSR or unusual environment)
 */
export function isUnknownPlatform() {
    return CURRENT_PLATFORM === Platform.UNKNOWN;
}
/**
 * Synchronous platform-aware module loader
 *
 * This is the recommended approach for both web and mobile platforms.
 * Uses factory functions to avoid dynamic imports that aren't supported in React Native.
 *
 * @param webFactory - Factory function for web module
 * @param mobileFactory - Factory function for mobile module
 * @returns The appropriate module instance
 *
 * @example
 * ```typescript
 * const service = loadPlatformModuleSync<MyService>(
 *   () => WebService.getInstance(),
 *   () => {
 *     const mobile = require('./mobile');
 *     return mobile.mobileService;
 *   }
 * );
 * ```
 */
export function loadPlatformModuleSync(webFactory, mobileFactory) {
    if (isMobile()) {
        try {
            return mobileFactory();
        }
        catch (error) {
            console.warn('Failed to load mobile module, falling back to web:', error);
        }
    }
    return webFactory();
}
